import React from "react";
import { Formik, FieldArray } from "formik";
import {
  Modal,
  InlineNotification,
  ButtonSet,
  Button,
  TextArea,
  TextInput,
  DatePickerInput,
  DatePicker,
  Form,
  FormGroup,
} from "carbon-components-react";
import { Add32, TrashCan32 } from "@carbon/icons-react";
import formatCurrency from "@eave/borrower/web_modules/utils/formatCurrency";
import {
  makeDatePickerEvent,
  makeEvent,
  moneyInputToNumber,
} from "web_modules/shared/FormHelpers/carbon-helpers";
import { formatPhone } from "web_modules/HLHLContacts";
import { stripPhoneNumberSymbols } from "@eave/shared/utils";
import { validationSchema } from "./validation";

interface Props {
  openModal: boolean;
  initialValues: Record<string, any>;
  onSubmit: (values: Record<string, any>) => void;
  onRequestClose: () => void;
}

const ConditionsPDFModalForm: React.FC<Props> = ({
  openModal,
  initialValues,
  onSubmit,
  onRequestClose,
}: Props) => {
  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ values, errors, touched, handleSubmit, handleChange, handleBlur, resetForm }) => {
        const {
          loan_number,
          property_address,
          prior_to_closing_conditions_list,
          borrowers_formatted_names,
          seller_id,
          lender_name,
          product_name,
          status,
          status_date,
          gross_loan_amount,
          purpose,
          occupancy,
          document_type,
          term_months,
          expiration,
          lock_expiration,
          appraisal_expiration,
          asset_expiration,
          credit_expiration,
          flood_expiration,
          income_expiration,
          mortgage_insurance_expiration,
          title_expiration,
          va_funding_fee_percentage,
          va_funding_fee_amount,
          underwriter_name,
          underwriter_email,
          underwriter_phone,
        } = values;
        return (
          <Modal
            passiveModal
            open={openModal}
            modalHeading="Edit Conditions PDF"
            shouldSubmitOnEnter={false}
            onRequestClose={() => {
              resetForm();
              onRequestClose();
            }}
            onBlur={() => {
              resetForm();
              onRequestClose();
            }}
          >
            <Form
              className="conditional-approval-pdf-form"
              onSubmit={(e: React.ChangeEvent<HTMLInputElement>) => {
                e.preventDefault();
                handleSubmit();
              }}
            >
              <InlineNotification
                hideCloseButton
                lowContrast
                kind="info"
                notificationType="inline"
                role="alert"
                title="Note:"
                subtitle="The values changed on this form will only take effect on the generated conditions PDF"
              />
              <FormGroup legendText="">
                <TextInput
                  id="loan_number"
                  name="loan_number"
                  size="sm"
                  type="text"
                  labelText="Loan #"
                  value={loan_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="property_address"
                  name="property_address"
                  size="sm"
                  type="text"
                  labelText="Property Address"
                  value={property_address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="borrowers_formatted_names"
                  name="borrowers_formatted_names"
                  size="sm"
                  type="text"
                  labelText="Borrower Name"
                  value={borrowers_formatted_names}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="seller_id"
                  name="seller_id"
                  size="sm"
                  type="text"
                  labelText="Seller ID"
                  value={seller_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="lender_name"
                  name="lender_name"
                  size="sm"
                  type="text"
                  labelText="Lender Name"
                  value={lender_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FormGroup>
              <FormGroup legendText="">
                <TextInput
                  id="product_name"
                  name="product_name"
                  size="sm"
                  type="text"
                  labelText="Product Name"
                  value={product_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="status"
                  name="status"
                  size="sm"
                  type="text"
                  labelText="Status"
                  value={status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <DatePicker
                  id="date-picker-status-date"
                  name="status_date"
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  value={status_date}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(makeDatePickerEvent("status_date", event));
                  }}
                  onBlur={handleBlur}
                >
                  <DatePickerInput
                    id="date-picker-status-date-input-id"
                    name="status_date"
                    labelText="Status Date"
                    type="text"
                    placeholder="yyyy-mm-dd"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
                <TextInput
                  id="gross_loan_amount"
                  name="gross_loan_amount"
                  size="sm"
                  type="text"
                  labelText="Gross Loan Amount"
                  value={formatCurrency(gross_loan_amount, 0, 2)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(moneyInputToNumber(e));
                  }}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="purpose"
                  name="purpose"
                  size="sm"
                  type="text"
                  labelText="Purpose"
                  value={purpose}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="occupancy"
                  name="occupancy"
                  size="sm"
                  type="text"
                  labelText="Occupancy"
                  value={occupancy}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="document_type"
                  name="document_type"
                  size="sm"
                  type="text"
                  labelText="Document Type"
                  value={document_type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="term_months"
                  name="term_months"
                  size="sm"
                  type="number"
                  labelText="Term (Months)"
                  value={term_months}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FormGroup>
              <FormGroup legendText="Conditions">
                <FieldArray
                  name="prior_to_closing_conditions_list"
                  render={arrayHelpers => (
                    <div>
                      {prior_to_closing_conditions_list?.map((condition: string, index: number) => (
                        <div key={index} style={{ marginTop: "10px" }}>
                          <TextArea
                            labelText=""
                            name={`prior_to_closing_conditions_list.${index}`}
                            rows={2}
                            value={condition}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <Button
                            iconDescription="Add New Condition"
                            renderIcon={Add32}
                            hasIconOnly
                            tooltipPosition="right"
                            kind="primary"
                            onClick={() => {
                              arrayHelpers.insert(index, "");
                            }}
                          />
                          <Button
                            iconDescription="Delete Condition"
                            renderIcon={TrashCan32}
                            hasIconOnly
                            tooltipPosition="right"
                            kind="danger"
                            onClick={() => {
                              arrayHelpers.remove(index);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                />
              </FormGroup>
              <FormGroup legendText="">
                <DatePicker
                  id="date-picker-expiration"
                  name="expiration"
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  value={expiration}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(makeDatePickerEvent("expiration", event))
                  }
                  onBlur={handleBlur}
                >
                  <DatePickerInput
                    id="date-picker-expiration-input-id"
                    name="expiration"
                    labelText="Overall Document Expiration Date"
                    placeholder="yyyy-mm-dd"
                    type="text"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
                <DatePicker
                  id="date-picker-lock-expiration"
                  name="lock_expiration"
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  value={lock_expiration}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(makeDatePickerEvent("lock_expiration", event))
                  }
                  onBlur={handleBlur}
                >
                  <DatePickerInput
                    id="date-picker-lock-expiration-input-id"
                    name="lock_expiration"
                    labelText="Lock Expiration"
                    placeholder="yyyy-mm-dd"
                    type="text"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
                <DatePicker
                  id="date-picker-appraisal-expiration"
                  name="appraisal_expiration"
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  value={appraisal_expiration}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(makeDatePickerEvent("appraisal_expiration", event))
                  }
                  onBlur={handleBlur}
                >
                  <DatePickerInput
                    id="date-picker-appraisal-expiration-input-id"
                    name="appraisal_expiration"
                    labelText="Appraisal Expiration"
                    placeholder="yyyy-mm-dd"
                    type="text"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
                <DatePicker
                  id="date-picker-asset-expiration"
                  name="asset_expiration"
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  value={asset_expiration}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(makeDatePickerEvent("asset_expiration", event))
                  }
                  onBlur={handleBlur}
                >
                  <DatePickerInput
                    id="date-picker-asset-expiration-input-id"
                    name="asset_expiration"
                    labelText="Asset Expiration"
                    placeholder="yyyy-mm-dd"
                    type="text"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
                <DatePicker
                  id="date-picker-credit-expiration"
                  name="credit_expiration"
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  value={credit_expiration}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(makeDatePickerEvent("credit_expiration", event))
                  }
                  onBlur={handleBlur}
                >
                  <DatePickerInput
                    id="date-picker-credit-expiration-input-id"
                    name="credit_expiration"
                    labelText="Credit Expiration"
                    placeholder="yyyy-mm-dd"
                    type="text"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
                <DatePicker
                  id="date-picker-flood-expiration"
                  name="flood_expiration"
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  value={flood_expiration}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(makeDatePickerEvent("flood_expiration", event))
                  }
                  onBlur={handleBlur}
                >
                  <DatePickerInput
                    id="date-picker-flood-expiration-input-id"
                    name="flood_expiration"
                    labelText="Flood Expiration"
                    placeholder="yyyy-mm-dd"
                    type="text"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
                <DatePicker
                  id="date-picker-income-expiration"
                  name="income_expiration"
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  value={income_expiration}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(makeDatePickerEvent("income_expiration", event))
                  }
                  onBlur={handleBlur}
                >
                  <DatePickerInput
                    id="date-picker-income-expiration-input-id"
                    name="income_expiration"
                    labelText="Income Expiration"
                    placeholder="yyyy-mm-dd"
                    type="text"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
                <DatePicker
                  id="date-picker-mortgage-insurance-expiration"
                  name="mortgage_insurance_expiration"
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  value={mortgage_insurance_expiration}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(makeDatePickerEvent("mortgage_insurance_expiration", event))
                  }
                  onBlur={handleBlur}
                >
                  <DatePickerInput
                    id="date-picker-mortgage-insurance-expiration-input-id"
                    name="mortgage_insurance_expiration"
                    labelText="Mortgage Insurance Expiration"
                    placeholder="yyyy-mm-dd"
                    type="text"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
                <DatePicker
                  id="date-picker-title-expiration"
                  name="title_expiration"
                  datePickerType="single"
                  dateFormat="Y-m-d"
                  value={title_expiration}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(makeDatePickerEvent("title_expiration", event))
                  }
                  onBlur={handleBlur}
                >
                  <DatePickerInput
                    id="date-picker-mortgage-insurance-expiration-input-id"
                    name="title_expiration"
                    labelText="Title Expiration"
                    placeholder="yyyy-mm-dd"
                    type="text"
                    size="sm"
                    pattern={null}
                  />
                </DatePicker>
                <TextInput
                  id="va_funding_fee_percentage"
                  name="va_funding_fee_percentage"
                  size="sm"
                  type="number"
                  labelText="VA Funding Fee %"
                  value={va_funding_fee_percentage}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="va_funding_fee_amount"
                  name="va_funding_fee_amount"
                  size="sm"
                  type="text"
                  labelText="VA Funding Fee Amount"
                  value={va_funding_fee_amount}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    handleChange(moneyInputToNumber(e));
                  }}
                />
                <TextInput
                  id="underwriter_name"
                  name="underwriter_name"
                  size="sm"
                  type="text"
                  labelText="Underwriter"
                  value={underwriter_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="underwriter_email"
                  name="underwriter_email"
                  size="sm"
                  type="text"
                  labelText="Underwriter Email"
                  value={underwriter_email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <TextInput
                  id="underwriter_phone"
                  name="underwriter_phone"
                  size="sm"
                  type="text"
                  labelText="Phone"
                  value={underwriter_phone && formatPhone(underwriter_phone)}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const e = makeEvent(
                      "underwriter_phone",
                      stripPhoneNumberSymbols(event.target.value),
                    );
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  invalid={errors.underwriter_phone && touched.underwriter_phone}
                  invalidText={errors.underwriter_phone}
                />
              </FormGroup>
              <ButtonSet>
                <Button
                  kind="secondary"
                  size="lg"
                  onClick={() => {
                    resetForm();
                    onRequestClose();
                  }}
                  style={{ maxWidth: "50%" }}
                >
                  Cancel
                </Button>
                <Button kind="primary" size="lg" type="submit" style={{ maxWidth: "50%" }}>
                  Export to PDF
                </Button>
              </ButtonSet>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
};

export default ConditionsPDFModalForm;
